import React, { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import ModalFooterWithActions from '../../modal-footer-actions';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import asModal from '../../as-modal';

import messages from './messages';
import { useUserSettingAdvancedHotkeys } from '../advanced-hotkeys/hooks';
import GeneralHotkeys from './general-hotkeys';
import SellerPresetHotkeys from './seller-preset-hotkeys';
import ShippingOrdersHotkeys from './shipping-orders-hotkeys';
import PrintingHotkeys from './printing-hotkeys';
import NavigationHotkeys from './navigation-hotkeys';

import style from './style.module.less';

const HotkeysModal = ({ onClose }) => {
  const { areAdvancedHotkeysEnabled, setAreAdvancedHotkeysEnabled } = useUserSettingAdvancedHotkeys();
  const [isAdvancedHotkeysChecked, setIsAdvancedHotkeysChecked] = useState(areAdvancedHotkeysEnabled);
  const toggleAdvancedHotkeysChecked = useCallback(() => {
    setIsAdvancedHotkeysChecked((isChecked) => !isChecked);
  }, []);
  const onSaveChanges = useCallback(() => {
    setAreAdvancedHotkeysEnabled(isAdvancedHotkeysChecked);
    onClose();
  }, [isAdvancedHotkeysChecked, setAreAdvancedHotkeysEnabled, onClose]);
  return (
    <Modal className={style['hotkeys-modal']}>
      <ModalHeader closeModal={onClose}>
        <FormattedMessage {...messages.hotkeys} />
      </ModalHeader>
      <ModalBody className={style.body}>
        <div className={style['basic-hotkeys']}>
          <div className={style.title}>
            <h3>
              <FormattedMessage {...messages.basicHotkeys} />
            </h3>
          </div>
          <div className={style['hotkeys-section']}>
            <span className={style['section-title']}>
              <FormattedMessage {...messages.general} />
            </span>
            <GeneralHotkeys />
            <span className={style['section-title']}>
              <FormattedMessage {...messages.customShippingPresets} />
            </span>
            <SellerPresetHotkeys />
          </div>
        </div>
        <div
          className={classnames(style['advanced-hotkeys-section'], {
            [style.disabled]: !isAdvancedHotkeysChecked,
          })}
        >
          <div className={style.title}>
            <h3>
              <FormattedMessage {...messages.advancedHotkeys} />
            </h3>
            <div>
              <span className={style['toggle-label']}>
                <FormattedMessage {...messages.advancedHotkeysEnabled} />
              </span>
              <CheckboxSlider
                checkboxId='advanced-hotkeys-toggle'
                checked={isAdvancedHotkeysChecked}
                className={style['checkbox-slider']}
                onChange={toggleAdvancedHotkeysChecked}
              />
            </div>
          </div>
          <div className={style['advanced-hotkey-columns']}>
            <div className={style['hotkeys-section']}>
              <span className={style['section-title']}>
                <FormattedMessage {...messages.shippingOrders} />
              </span>
              <ShippingOrdersHotkeys />
              <span className={style['section-title']}>
                <FormattedMessage {...messages.printingDocuments} />
              </span>
              <PrintingHotkeys />
            </div>
            <div className={style['hotkeys-section']}>
              <span className={style['section-title']}>
                <FormattedMessage {...messages.navigation} />
              </span>
              <NavigationHotkeys />
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooterWithActions
        actionMessage={<FormattedMessage {...messages.saveChanges} />}
        actionTheme='primary'
        cancelMessage={<FormattedMessage {...messages.cancel} />}
        onAction={onSaveChanges}
        onCancel={onClose}
      />
    </Modal>
  );
};

HotkeysModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default asModal(HotkeysModal);
